<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { IMPROVEMENTS_ANCHORS } from '@/modules/onboarding/scenarios/improvements/anchors'
import { useDisplay } from 'vuetify/framework'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import SearchableSelectList, { type ISearchableSelectItem } from '@/components/createdUi/SearchableSelectList.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import FileAttach from '@/modules/files/components/FileAttach.vue'
import { useImprovementsStore } from '@/modules/improvements/stores/improvementsStore'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'

const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'created'): void
}>()

const { xs, smAndDown } = useDisplay()
const improvementsStore = useImprovementsStore()

const title = ref('')
const selectedTypeId = ref<string | null>(null)
const showTypeMenu = ref(false)
const text = ref('')
const expectedResult = ref('')
const isAnonymous = ref(false)
const attachedFiles = ref<File[]>([])
const fileAttachRef = ref<InstanceType<typeof FileAttach> | null>(null)
const showUnsavedChangesDialog = ref(false)



const isOpen = computed({
  get: () => props.modelValue,
  set: (val: boolean) => emit('update:modelValue', val),
})

const isSubmitting = ref(false)

const typeSelectItems = computed<ISearchableSelectItem[]>(() => {
  return improvementsStore.availableSuggestionTypes.map(t => ({
    id: t.id,
    label: t.name,
  }))
})

const selectedTypeName = computed(() => {
  const found = improvementsStore.availableSuggestionTypes.find(t => t.id === selectedTypeId.value)
  return found ? found.name : ''
})

function onSelectType(item: ISearchableSelectItem) {
  selectedTypeId.value = String(item.id)
  showTypeMenu.value = false
}

function onClearType() {
  selectedTypeId.value = null
}

const isValid = computed(() => {
  return (
    title.value.trim().length > 0 &&
    selectedTypeId.value !== null &&
    text.value.trim().length > 0 &&
    expectedResult.value.trim().length > 0
  )
})

const isDirty = computed(() => {
  return (
    title.value.trim().length > 0 ||
    selectedTypeId.value !== null ||
    text.value.trim().length > 0 ||
    expectedResult.value.trim().length > 0 ||
    isAnonymous.value ||
    attachedFiles.value.length > 0
  )
})

onMounted(async () => {
  if (!improvementsStore.availableSuggestionTypes.length) {
    await improvementsStore.fetchAvailableSuggestionTypes()
  }
})

watch(isOpen, (newVal) => {
  if (newVal) {
    resetForm()
    if (!improvementsStore.availableSuggestionTypes.length) {
      improvementsStore.fetchAvailableSuggestionTypes()
    }
  }
})

function resetForm() {
  title.value = ''
  selectedTypeId.value = null
  showTypeMenu.value = false
  text.value = ''
  expectedResult.value = ''
  isAnonymous.value = false
  attachedFiles.value = []
  showUnsavedChangesDialog.value = false
}

function handleClose() {
  if (isDirty.value) {
    showUnsavedChangesDialog.value = true
  } else {
    forceClose()
  }
}

function forceClose() {
  showUnsavedChangesDialog.value = false
  isOpen.value = false
}

function triggerFileSelect() {
  fileAttachRef.value?.openDialog()
}

function onFilesUpdate(newFiles: File[]) {
  attachedFiles.value = newFiles
}

function onFileRemoved(index: number) {
  attachedFiles.value.splice(index, 1)
}

async function onSubmit() {
  if (!isValid.value || isSubmitting.value) return
  isSubmitting.value = true

  try {
    const created = await improvementsStore.createSuggestion({
      title: title.value.trim(),
      suggestion_type: { id: selectedTypeId.value! },
      text: text.value.trim(),
      expected_result: expectedResult.value.trim(),
      is_anonymous: isAnonymous.value,
    })

    if (created && created.id) {
      if (attachedFiles.value.length) {
        await improvementsStore.uploadSuggestionFiles(created.id, attachedFiles.value)
      }
      emit('created')
      forceClose()
    }
  } finally {
    isSubmitting.value = false
  }
}

const onDialogDismissAttempt = useDialogDismissGuard(handleClose)
</script>

<template>
  <VDialog
    v-model="isOpen"
    max-width="44rem"
    content-class="entity-form-create-dialog"
    persistent
    @click:outside="onDialogDismissAttempt"
    @keydown.esc.stop="onDialogDismissAttempt"
  >
    <VCard>
      <VCardTitle class="d-flex pa-6">
        <div class="form_card d-flex justify-space-between w-100">
          <div class="form_card__title">
            <span class="text-h4">Новое предложение по улучшению</span>
          </div>
        </div>
        <CustomCloseButton :close-action="handleClose" />
      </VCardTitle>

      <VForm @submit.prevent="onSubmit">
        <div class="entity-form-scroll-body">
          <VCol class="d-flex flex-column gap-4 px-0">
            <VTextField
              v-model="title"
              variant="outlined"
              density="comfortable"
              placeholder="Кратко сформулируйте идею"
              persistent-placeholder
            >
              <template #label>
                <span>Предложение</span>
              </template>
            </VTextField>

            <!-- Дропдаун "Вид ППУ" через SearchableSelectList из createdUi -->
            <VMenu
              v-model="showTypeMenu"
              eager
              :close-on-content-click="false"
              :max-height="350"
              content-class="entity-form-menu"
            >
              <template #activator="{ props: menuProps }">
                <VTextField
                  :model-value="selectedTypeName"
                  :data-onboarding="IMPROVEMENTS_ANCHORS.create.type"
                  class="invisible-readonly cursor-pointer"
                  :append-inner-icon="showTypeMenu ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
                  variant="outlined"
                  density="comfortable"
                  placeholder="Выберите вид ППУ"
                  persistent-placeholder
                  readonly
                  clearable
                  v-bind="menuProps"
                  @click:append-inner.stop="showTypeMenu = !showTypeMenu"
                  @click:clear="onClearType"
                >
                  <template #label>
                    <span>Вид ППУ</span>
                  </template>
                </VTextField>
              </template>

              <SearchableSelectList
                :items="typeSelectItems"
                placeholder="Поиск вида ППУ..."
                @select="onSelectType"
              />
            </VMenu>

            <VTextarea
              v-model="text"
              density="comfortable"
              rows="3"
              variant="outlined"
              placeholder="Опишите текущий процесс, его проблему и предлагаемое изменение"
              persistent-placeholder
            >
              <template #label>
                <span>Описание</span>
              </template>
            </VTextarea>

            <VTextarea
              v-model="expectedResult"
              density="comfortable"
              rows="3"
              variant="outlined"
              placeholder="Опишите, что изменится после реализации предложения"
              persistent-placeholder
            >
              <template #label>
                <span>Результат</span>
              </template>
            </VTextarea>

            <VCheckbox
              v-model="isAnonymous"
              :data-onboarding="IMPROVEMENTS_ANCHORS.create.anonymous"
              class="align-self-start"
              label="Анонимно"
              density="compact"
              hide-details
            />

            <!-- Прикрепление файлов как в форме НС и Заявок -->
            <div class="d-flex flex-column gap-2">
              <div
                class="d-flex gap-4"
                :class="xs ? 'flex-column' : 'align-center flex-wrap'"
              >
                <VBtn
                  :block="xs"
                  :class="{ 'align-self-start flex-shrink-1': !xs }"
                  variant="outlined"
                  color="primary"
                  prepend-icon="ri-attachment-line"
                  @click="triggerFileSelect"
                >
                  Прикрепить файлы
                </VBtn>
                <span
                  v-if="!smAndDown"
                  class="text-body-2 paste-hint"
                >или вставьте файлы из буфера (Ctrl+V)</span>
              </div>

              <FileAttach
                ref="fileAttachRef"
                :files="attachedFiles"
                accept="*"
                @update:files="onFilesUpdate"
              />
            </div>
          </VCol>
        </div>

        <footer class="d-flex gap-2" :class="xs ? 'flex-column' : 'justify-end'">
          <VBtn
            type="submit"
            variant="elevated"
            color="primary"
            :block="xs"
            :disabled="!isValid"
            :loading="isSubmitting"
          >
            Предложить
          </VBtn>
        </footer>
      </VForm>
    </VCard>
  </VDialog>

  <!-- Диалог подтверждения закрытия при несохранённых изменениях -->
  <ConfirmDialog
    v-model:is-dialog-visible="showUnsavedChangesDialog"
    confirmation-question="Закрыть без сохранения?"
    confirmation-text="Процесс создания предложения по улучшению будет прерван, данные не сохранятся"
    confirm-btn="Закрыть"
    reject-btn="Отменить"
    @confirm="forceClose"
  />
</template>
